/**
 * EmptyState Component
 *
 * GAP-008: Empty states with guidance
 *
 * Display when a list or section has no content.
 * Features:
 * - Contextual guidance hints
 * - Getting started steps
 * - Feature discovery integration
 * - First-use vs filtered empty states
 */

import type { ReactNode } from 'react';
import {
  FolderOpenIcon,
  LightBulbIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { cn } from '../../utils/cn';
import { Button } from '../Button';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: ReactNode;
  variant?: 'primary' | 'secondary';
}

/** Guidance hint for empty states */
export interface EmptyStateHint {
  /** Hint text */
  text: string;
  /** Optional link */
  href?: string;
  /** Optional click handler */
  onClick?: () => void;
}

/** Getting started step */
export interface EmptyStateStep {
  /** Step number (1, 2, 3...) */
  number: number;
  /** Step title */
  title: string;
  /** Step description */
  description?: string;
  /** Whether this step is completed */
  completed?: boolean;
  /** Action for this step */
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}

export interface EmptyStateProps {
  /** Icon to display (defaults to FolderOpenIcon) */
  icon?: ReactNode;
  /** Main heading */
  title: string;
  /** Description text */
  description?: string;
  /** Primary action button */
  action?: EmptyStateAction;
  /** Multiple actions - alias for action + secondaryActions */
  actions?: EmptyStateAction[];
  /** Secondary actions */
  secondaryActions?: EmptyStateAction[];
  /** Additional className */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** GAP-008: Contextual hints for guidance */
  hints?: EmptyStateHint[];
  /** GAP-008: Getting started steps */
  steps?: EmptyStateStep[];
  /** GAP-008: Illustration variant for more visual appeal */
  illustration?: 'folder' | 'document' | 'search' | 'users' | 'custom';
  /** GAP-008: Whether this is a filtered empty state (vs first-use) */
  isFiltered?: boolean;
  /** GAP-008: Clear filters callback for filtered state */
  onClearFilters?: () => void;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  actions,
  secondaryActions,
  className,
  size = 'md',
  hints,
  steps,
  illustration,
  isFiltered,
  onClearFilters,
}: EmptyStateProps) {
  // Merge actions array with action/secondaryActions for backward compatibility
  const allActions = actions || (action ? [action, ...(secondaryActions || [])] : secondaryActions || []);
  const primaryAction = allActions[0];
  const otherActions = allActions.slice(1);
  const sizeClasses = {
    sm: {
      container: 'py-6',
      icon: 'h-10 w-10',
      title: 'text-base',
      description: 'text-sm max-w-xs',
    },
    md: {
      container: 'py-12',
      icon: 'h-12 w-12',
      title: 'text-lg',
      description: 'text-sm max-w-sm',
    },
    lg: {
      container: 'py-16',
      icon: 'h-16 w-16',
      title: 'text-xl',
      description: 'text-base max-w-md',
    },
  };

  const sizes = sizeClasses[size];

  // Render illustration if specified
  const renderIllustration = () => {
    if (icon) return icon;
    if (illustration === 'custom') return null;

    // Default illustrations by type
    return <FolderOpenIcon className="h-full w-full" />;
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizes.container,
        className
      )}
    >
      {/* Icon/Illustration - GAP-015: text-mv-gray-500 for WCAG AA contrast */}
      <div className={cn('mb-4 text-mv-gray-500', sizes.icon)}>
        {renderIllustration()}
      </div>

      {/* Title */}
      <h3 className={cn('font-medium text-mv-gray-900', sizes.title)}>{title}</h3>

      {/* Description */}
      {description && (
        <p className={cn('mt-1 text-mv-gray-500', sizes.description)}>{description}</p>
      )}

      {/* GAP-008: Filtered state with clear button */}
      {isFiltered && onClearFilters && (
        <button
          onClick={onClearFilters}
          className="mt-3 text-sm text-mv-blue-600 hover:text-mv-blue-700 hover:underline"
        >
          Filters wissen
        </button>
      )}

      {/* GAP-008: Getting Started Steps */}
      {steps && steps.length > 0 && (
        <div className="mt-8 w-full max-w-md">
          <div className="rounded-lg border border-mv-gray-200 bg-mv-gray-50 p-4">
            <h4 className="flex items-center gap-2 text-sm font-medium text-mv-gray-700 mb-4">
              <LightBulbIcon className="h-5 w-5 text-mv-amber-500" />
              Aan de slag
            </h4>
            <div className="space-y-3">
              {steps.map((step) => (
                <div
                  key={step.number}
                  className={cn(
                    'flex items-start gap-3 text-left',
                    step.completed && 'opacity-60'
                  )}
                >
                  {/* Step number/check */}
                  <div
                    className={cn(
                      'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium',
                      step.completed
                        ? 'bg-mv-green-100 text-mv-green-700'
                        : 'bg-mv-blue-100 text-mv-blue-700'
                    )}
                  >
                    {step.completed ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      step.number
                    )}
                  </div>
                  {/* Step content */}
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        step.completed ? 'text-mv-gray-500 line-through' : 'text-mv-gray-900'
                      )}
                    >
                      {step.title}
                    </p>
                    {step.description && (
                      <p className="text-xs text-mv-gray-500 mt-0.5">{step.description}</p>
                    )}
                    {step.action && !step.completed && (
                      step.action.href ? (
                        <Link
                          to={step.action.href}
                          className="inline-flex items-center gap-1 mt-1 text-xs text-mv-blue-600 hover:text-mv-blue-700"
                        >
                          {step.action.label}
                          <ArrowRightIcon className="h-3 w-3" />
                        </Link>
                      ) : (
                        <button
                          onClick={step.action.onClick}
                          className="inline-flex items-center gap-1 mt-1 text-xs text-mv-blue-600 hover:text-mv-blue-700"
                        >
                          {step.action.label}
                          <ArrowRightIcon className="h-3 w-3" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {allActions.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {primaryAction && (
            primaryAction.href ? (
              <Link
                to={primaryAction.href}
                className="inline-flex items-center gap-2 rounded bg-mv-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-mv-blue-900"
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Link>
            ) : (
              <Button
                variant={primaryAction.variant || 'primary'}
                onClick={primaryAction.onClick}
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            )
          )}
          {otherActions.map((otherAction, idx) => (
            otherAction.href ? (
              <Link
                key={idx}
                to={otherAction.href}
                className="inline-flex items-center gap-2 rounded bg-mv-gray-100 px-4 py-2 text-sm font-medium text-mv-gray-700 hover:bg-mv-gray-200"
              >
                {otherAction.icon}
                {otherAction.label}
              </Link>
            ) : (
              <Button
                key={idx}
                variant={otherAction.variant || 'secondary'}
                onClick={otherAction.onClick}
              >
                {otherAction.icon}
                {otherAction.label}
              </Button>
            )
          ))}
        </div>
      )}

      {/* GAP-008: Contextual Hints */}
      {hints && hints.length > 0 && (
        <div className="mt-6 w-full max-w-sm">
          <div className="space-y-2">
            {hints.map((hint, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 text-left text-xs text-mv-gray-500"
              >
                <LightBulbIcon className="h-4 w-4 flex-shrink-0 text-mv-amber-500" />
                {hint.href ? (
                  <Link to={hint.href} className="hover:text-mv-blue-600 hover:underline">
                    {hint.text}
                  </Link>
                ) : hint.onClick ? (
                  <button
                    onClick={hint.onClick}
                    className="text-left hover:text-mv-blue-600 hover:underline"
                  >
                    {hint.text}
                  </button>
                ) : (
                  <span>{hint.text}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * GAP-008: Empty state for search results
 *
 * Specialized empty state for "no search results" scenario.
 */
export interface SearchEmptyStateProps {
  /** Search query that returned no results */
  query: string;
  /** Suggestions for improving the search */
  suggestions?: string[];
  /** Clear search callback */
  onClearSearch?: () => void;
  /** Additional class names */
  className?: string;
}

export function SearchEmptyState({
  query,
  suggestions,
  onClearSearch,
  className,
}: SearchEmptyStateProps) {
  return (
    <EmptyState
      icon={
        <svg
          className="h-full w-full"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
      }
      title="Geen resultaten"
      description={`Geen resultaten gevonden voor "${query}"`}
      className={className}
      hints={
        suggestions?.map((suggestion) => ({ text: suggestion })) || [
          { text: 'Controleer de spelling van uw zoekterm' },
          { text: 'Probeer een andere zoekterm' },
          { text: 'Gebruik minder specifieke zoektermen' },
        ]
      }
      action={
        onClearSearch
          ? {
              label: 'Zoekopdracht wissen',
              onClick: onClearSearch,
              variant: 'secondary',
            }
          : undefined
      }
    />
  );
}

/**
 * GAP-008: First-use empty state with onboarding
 *
 * Shows when a user sees a section for the first time.
 */
export interface FirstUseEmptyStateProps {
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
  /** Getting started steps */
  steps: EmptyStateStep[];
  /** Primary CTA */
  action?: EmptyStateAction;
  /** Custom icon */
  icon?: ReactNode;
  /** Additional class names */
  className?: string;
}

export function FirstUseEmptyState({
  title,
  description,
  steps,
  action,
  icon,
  className,
}: FirstUseEmptyStateProps) {
  return (
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      steps={steps}
      action={action}
      className={className}
      size="lg"
    />
  );
}
